import { Component, OnInit, Input, Output, EventEmitter, OnChanges, ViewChild, AfterViewInit } from '@angular/core';
import { Question } from '../../models/question.class';
import { FormControl } from '@angular/forms';
import { startWith } from 'rxjs/operators/startWith';
import { map } from 'rxjs/operators/map';
import { Router } from '@angular/router';
import { MatAutocompleteTrigger } from '@angular/material';

import * as _ from 'lodash';
import { DataEnadisService } from '../../services/data-enadis/data-enadis.service';
import { ConfigVariable } from '../../models/ConfigVariable.class';

/**
 * Select a question using topics or groups (multi-autocompletes)
 */
@Component({
  selector: 'app-question-dropdown-selection',
  templateUrl: './question-dropdown-selection.component.html',
  styleUrls: ['./question-dropdown-selection.component.scss']
})
export class QuestionDropdownSelectionComponent implements OnInit, OnChanges, AfterViewInit {
  /** List of questions */
  @Input() allVariables: Question[];
  /** Configuration for variables and selects */
  @Input() config: ConfigVariable;
  /** Emitter when question changes */
  @Output() changeQuestion = new EventEmitter<any>();
  /** Emitter when population group changes */
  @Output() changeCollapseBy = new EventEmitter<string>();
  /** List of population groups */
  groups: PopulationGroup[];
  /** Keeps the found group */
  foundGroup: PopulationGroup;
  /** Name of the selected group */
  selectedGroup: string;
  /** Form control to search group */
  searchGroup: FormControl;
  /** Form control to search ambit */
  searchAmbito: FormControl;
  /** Form control to search topic */
  searchTopic: FormControl;
  /** Form control to search question */
  searchQuestion: FormControl;
  /** Form control to search questionnaires */
  searchQuestionnaires: FormControl;
  /** List of ambits */
  allAmbito: Array<any> = [];
  /** List of ambits names */
  ambitoNames: Array<any> = [];
  /** List of group names */
  groupNames: Array<any> = [];
  /** List of topics */
  allTopics: Question[];
  /** List of variables filterd by group */
  variablesFromGroup: Question[];
  /** List of questions that correspond to ambit */
  ambitoTopics: Question[];
  /** List of questions that correspond to questionnarie */
  questionnaires: Question[];
  /** List of questions */
  variables: Question[];
  /** List of topics */
  topics: {}[];
  /** List of filtered groups */
  filteredGroups: any = [];
  /** List of filtered ambits */
  filteredAmbitos: any = [];
  /** List of filterd topics */
  filteredTopics: any = [];
  /** List of filtered questionnaires */
  filteredQuestionnaires: any = [];
  /** List of filtered variables */
  filteredVariables: any = [];
  /** Selected question */
  selectedQuestion: Question;
  /** Identified population group */
  identifiedGroup: PopulationGroup;
  /** Expansion factor */
  collapseBy: string;
  /** Name of selected ambit */
  selectedAmbito: string;
  /** Selected topic */
  selectedTopic: { topic: string };
  /** Selected questionnarie object */
  selectedQuestionnarie: any;
  /** Autocomplete for groups */
  @ViewChild('mat0', { read: MatAutocompleteTrigger }) trigger: MatAutocompleteTrigger;
  /** Autocomplete for ambits */
  @ViewChild('mat1', { read: MatAutocompleteTrigger }) trigger1: MatAutocompleteTrigger;
  /** Autocompelte for topics */
  @ViewChild('mat2', { read: MatAutocompleteTrigger }) trigger2: MatAutocompleteTrigger;
  /** Autocomplete for questionnaire */
  @ViewChild('mat3', { read: MatAutocompleteTrigger }) trigger3: MatAutocompleteTrigger;
  /** Autocomplete for questions */
  @ViewChild('mat4', { read: MatAutocompleteTrigger }) trigger4: MatAutocompleteTrigger;

  /** Init component, inject Router and DataENADISService */
  constructor(private router: Router, private dbService: DataEnadisService) { }

  /**Set default form controls, init autocompletes */
  ngOnInit() {
    this.groups = this.dbService.getGroups();
    this.searchGroup = new FormControl();
    this.searchAmbito = new FormControl();
    this.searchTopic = new FormControl();
    this.searchQuestion = new FormControl();
    this.searchQuestionnaires = new FormControl();

    // Autocomplete of groups
    this.filteredGroups = this.searchGroup.valueChanges
      .pipe(
        startWith(''),
        map(group => {
          if (Array.isArray(this.groupNames)) {
            return group ? this.filterGroups(group) : this.groupNames.slice().sort();
          } else {
            return [];
          }
        })
      );

    // Autocomplete of ambitos
    this.filteredAmbitos = this.searchAmbito.valueChanges
      .pipe(
        startWith(''),
        map(ambito => {
          if (Array.isArray(this.ambitoNames)) {
            return ambito ? this.filterAmbitos(ambito) : this.ambitoNames.slice();
          } else {
            return [];
          }
        })
      );

    // Autocomplete of topics
    this.filteredTopics = this.searchTopic.valueChanges
      .pipe(
        startWith(''),
        map(topic => {
          if (Array.isArray(this.allTopics)) {
            return topic ? this.filterTopics(topic) : this.allTopics.slice();
          } else {
            return [];
          }
        })
      );

    // Autocomplete of questionnaires
    this.filteredQuestionnaires = this.searchQuestionnaires.valueChanges
      .pipe(
        startWith(''),
        map(questionn => {
          if (Array.isArray(this.questionnaires)) {
            return questionn ? this.filterQuestionnaires(questionn) : this.questionnaires.slice();
          } else {
            return [];
          }
        })
      );

    // Autocomplete of variables
    this.filteredVariables = this.searchQuestion.valueChanges
      .pipe(
        startWith(''),
        map(variable => {
          if (Array.isArray(this.variables)) {
            return variable ? this.filterVariables(variable) : this.variables.slice().sort(this.sortVariables);
          } else {
            return [];
          }
        })
      );
  }

  /**
   * Called when input changes
   * If allVariables: init options and set default values
   * If config: set default values
   */
  ngOnChanges(changes) {
    if (changes.hasOwnProperty('allVariables')) {
      this.initOptions();
      this.setDefaultValues();
    }
    if (changes.hasOwnProperty('config')) {
      this.setDefaultValues();
    }
  }

  /**
   * After view is defined, set values for autocompletes
   */
  ngAfterViewInit() {
    if (this.config && this.config.mode === 'grupos') {
      this.trigger.panelClosingActions.subscribe(e => {
        this.searchGroup.setValue(this.selectedGroup);
        this.searchTopic.setValue(this.selectedQuestion);
        this.searchQuestion.setValue(this.selectedQuestion);
      });
      this.trigger2.panelClosingActions.subscribe(() => {
        this.searchTopic.setValue(this.selectedQuestion);
      });
    } else {
      this.trigger1.panelClosingActions.subscribe(() => {
        this.searchAmbito.setValue(this.selectedAmbito);
      });
      this.trigger3.panelClosingActions.subscribe(() => {
        this.searchQuestionnaires.setValue(this.selectedQuestion);
      });
    }
    this.trigger4.panelClosingActions.subscribe(() => {
      this.searchQuestion.setValue(this.selectedQuestion);
    });
  }

  /**
   * Init default values for autocompletes
   */
  setDefaultValues() {
    if (this.config) {
      switch (this.config.mode) {
        case 'grupos':
          let searchSlug;
          if (this.config.selectedTopic) {
            searchSlug = this.getSlug(this.config.selectedTopic);
            this.searchGroup.setValue(searchSlug);
          } else if (this.config.selectedQuestion) {
            searchSlug = this.getSlug(this.config.selectedQuestion.topic);
            this.searchGroup.setValue(searchSlug);
          }

          if (this.config.selectedQuestion) {
            this.searchTopic.setValue(this.config.selectedQuestion);
          }
          break;
        case 'ambitos':
          if (this.config.selectedTopic) {
            this.searchAmbito.setValue(this.config.selectedTopic);
          } else if (this.config.selectedQuestion) {
            this.searchAmbito.setValue(this.config.selectedQuestion.Ambito);
          }
          if (this.config.selectedQuestion) {
            this.searchQuestionnaires.setValue(this.config.selectedQuestion);
          }
          break;
      }
      if (this.config.selectedQuestion) {
        this.selectedQuestion = this.config.selectedQuestion;
        this.searchQuestion.setValue(this.selectedQuestion);
      }
    }
  }

  /**
   * Set variables list and get groups or "ambitos"
   */
  initOptions() {
    // No data for variables
    if (!Array.isArray(this.allVariables) || this.allVariables.length < 1) {
      return null;
    }
    this.allVariables = this.allVariables.map(obj => new Question(obj));
    this.variables = this.allVariables;
    // No config
    if (!this.config) {
      return null;
    }
    if (this.config.mode === 'grupos') {
      this.getGroups(this.allVariables);
    } else if (this.config.mode === 'ambitos') {
      this.getAmbito(this.allVariables);
    }
  }

  /** Sort variables using shortName */
  sortVariables(a, b) {
    return a.shortName.localeCompare(b.shortName, undefined, { numeric: true });
  }

  /** Sort groups by name */
  sortGroups(a, b) {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  }

  /** Filter variables using short name */
  filterVariables(name: string) {
    if (typeof name === 'string') {
      return this.variables.filter(variable =>
        variable.shortName.toLowerCase().indexOf(name.toLowerCase()) > -1);
    } else if (name['shortName']) {
      return this.variables.filter(variable =>
        variable.shortName.toLowerCase().indexOf((name['question'] as string).toLowerCase()) > -1).sort(this.sortVariables);
    }
  }

  /** Filter groups using nam. Change expansion factor */
  filterGroups(name: string) {
    if (typeof name === 'string') {
      this.selectedGroup = name;
      if (this.selectedGroup) {
        this.foundGroup = _.find(this.groups, { name: this.selectedGroup });
        if (this.collapseBy && this.foundGroup) {
          this.collapseBy = this.foundGroup.collapseBy;
          this.changeCollapseBy.emit(this.collapseBy);
        }
      }
      this.setGroup(name);
      this.filterTopics(name);
      this.searchTopic.setValue(undefined);
      this.searchQuestion.setValue(undefined);

      return this.groupNames.filter(gr =>
        gr.toLowerCase().indexOf(name.toLowerCase()) > -1).sort();
    }
  }

  /** Filter ambits by name */
  filterAmbitos(name: string) {
    if (typeof name === 'string') {
      this.selectedAmbito = name;

      this.setAmbito(name);

      this.searchQuestionnaires.setValue(undefined);
      this.searchQuestion.setValue(undefined);

      return this.ambitoNames.filter(amb =>
        amb.toLowerCase().indexOf(name.toLowerCase()) > -1);
    }
  }

  /** Filter topics by name. Update list of variables */
  filterTopics(name) {
    if (typeof name === 'string') {
      this.variables = this.variablesFromGroup.filter(function (q) {
        return q.subtopic === name;
      });
      this.allTopics.filter(st =>
        st.subtopic.toLowerCase().indexOf(name.toLowerCase()) > -1);
      if (name === 'Población de 18 años y más (opiniones y experiencias de discriminación)') {
        this.allTopics = this.allTopics.filter(function (question) {
          const result = question.subtopic === 'Características sociodemográficas' ? false : true;
          return result;
        });
      }
    } else if (name['subtopic']) {
      // variable is a selected object
      this.selectedTopic = name;
      this.variables = this.variablesFromGroup.filter(function (q) {
        return q.subtopic === name['subtopic'];
      });
      this.allTopics.filter(top =>
        top.subtopic.toLowerCase().indexOf((name['subtopic'] as string).toLowerCase()) > -1);
      if (name.topic === 'Cuestionario de opinión y experiencias') {
        this.allTopics = this.allTopics.filter(function (question) {
          const result = question.subtopic === 'Características sociodemográficas' ? false : true;
          return result;
        });
      }
    }
    return this.allTopics;
  }

  /** Filter questionnaires by name. Update variables */
  filterQuestionnaires(name: string) {
    if (typeof name === 'string') {
      this.variables = this.ambitoTopics.filter(function (q) {
        return q.subtopic === name;
      });
      return this.questionnaires.filter(que =>
        que.topic.toLowerCase().indexOf(name.toLowerCase()) > -1);
    } else {
      // variable is a selected object
      this.selectedQuestionnarie = name;
      this.variables = this.ambitoTopics.filter(function (q) {
        return q.subtopic === name['subtopic'];
      });
      return this.questionnaires.filter(que =>
        que.topic.toLowerCase().indexOf((name['topic'] as string).toLowerCase()) > -1);
    }
  }

  /** Get ambits of questions */
  getAmbito(questions) {
    const qAmbito = questions.filter(function (q) {
      if (q && q.Ambito && q.Ambito.includes(',')) {
        q.Ambito = q.Ambito.split(',');
      }
      return q.Ambito !== '';
    });

    this.variables = qAmbito;
    this.allAmbito = qAmbito;
    this.allVariables = qAmbito;
    this.setAmbito(this.config.selectedTopic);

    const justAmbito = _.flatten(qAmbito.map(function (a) {
      return a.Ambito;
    }));

    this.ambitoNames = _.uniq(justAmbito);
  }

  /** Get groups of questions */
  getGroups(questions) {
    const qgrupos = questions.filter(function (q) {
      if (q && q.grupos && q.grupos.includes(',')) {
        q.grupos = q.grupos.split(',');
      }
      return q.grupos !== '';
    });

    this.allVariables = qgrupos;
    this.variables = qgrupos;
    const setParam = this.getSlug(this.config.selectedTopic);
    this.setGroup(setParam);

    this.groups.forEach(function (g) {
      if (this.groupNames.indexOf(g.name) === -1) {
        this.groupNames.push(g.name);
      }
    }.bind(this));
  }

  /** Get sa group filtered by slug or name*/
  getSlug(name) {
    const filteredSlug = this.groups.filter(function (g) {
      return g.name === name || g.slug === name;
    })[0]['name'];

    return filteredSlug;
  }

  /** Set a variable */
  setVariable(event) {
    this.selectedQuestion = this.searchQuestion.value;
    const grupo = this.config.mode === 'grupos' ? this.identifiedGroup.name :
      this.selectedQuestionnarie ? this.selectedQuestionnarie.topic : this.selectedQuestion.topic;
    const config = {
      selectedQuestion: this.selectedQuestion,
      selectedGroup: grupo
    };
    this.changeQuestion.emit(config);
  }

  /** Set a group. Update list of variables */
  setGroup(value) {
    this.identifiedGroup = this.groups.filter(function (g) {
      return g.name === value;
    })[0];

    const factor = this.identifiedGroup.collapseBy;

    value = this.identifiedGroup.slug;
    this.variablesFromGroup = this.allVariables.filter(function (g: Question) {
      let result = g.grupos === value || (Array.isArray(g.grupos) && _.includes(g.grupos, value));
      if (factor) {
        result = result && (factor === g.collapseBy ||
          (_.includes(['FACTOR_SDE'], g.collapseBy) && this.selectedTopic !== 'Cuestionario de opiniones y experiencias'));
      }
      return result;
    }.bind(this));

    this.allTopics = _.uniqBy(this.variablesFromGroup, 'subtopic');
    this.searchTopic.setValue(undefined);
  }

  /** Set ambit. Update list of variables */
  setAmbito(value) {
    this.ambitoTopics = this.allAmbito.filter(function (fa) {
      if (fa && fa.grupos && fa.grupos.includes(',')) {
        fa.grupos = fa.grupos.split(',');
      }
      return fa.grupos !== '' && fa.Ambito === value || (Array.isArray(fa.Ambito) && _.includes(fa.Ambito, value));
    });
    this.variables = this.ambitoTopics;

    this.questionnaires = _.uniqBy(this.ambitoTopics, 'topic');
  }

  /** Display function for autocomplete groups */
  displayFn0(gr?: Question): string | undefined {
    let grp;
    if (this.groupNames) {
      grp = this.groupNames[this.groupNames.indexOf(gr)];
    } else {
      grp = gr;
    }

    return grp ? grp : undefined;
  }

  /** Display function for autocomplete ambits */
  displayFn1(amb?: Question): string | undefined {
    let context;
    if (this.ambitoNames) {
      context = this.ambitoNames[this.ambitoNames.indexOf(amb)];
    } else {
      context = amb;
    }
    return context ? context : undefined;
  }

  /** Display function for autocomplete questions */
  displayFn2(question?: Question): string | undefined {
    return question ? question.shortName : undefined;
  }

  /** Display function for autocomplete subtopics */
  displayFnSubTopic(st): string | undefined {
    return st ? st.subtopic : undefined;
  }

  /** Display function for autocomplete topic */
  displayFn3(qn?: Question): string | undefined {
    return qn ? qn.topic : undefined;
  }

  /** Display function for autocomplete questions */
  displayFn(variable?: Question): string | undefined {
    return variable ? variable.shortName : undefined;
  }

  /** Erase search group value autocomplete */
  eraseSearchGroup() {
    this.searchGroup.setValue(undefined);
  }

  /** Erase search ambito value autocomplete */
  eraseSearchAmbito() {
    this.searchAmbito.setValue(undefined);
  }

  /** Erase search topic value autocomplete */
  eraseSearchTopics() {
    this.searchTopic.setValue(undefined);
  }

  /** Erase search questionnaries value autocomplete */
  eraseSearchQ() {
    this.searchQuestionnaires.setValue(undefined);
  }

  /** Erase search question value autocomplete */
  eraseSearch() {
    this.searchQuestion.setValue(undefined);
  }
}



// WEBPACK FOOTER //
// ./src/app/components/question-dropdown-selection/question-dropdown-selection.component.ts